import { Type as T } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';
import { validateField } from '../index.js';
import { EDeviceManagerType, ELedPanelOutputResolution, ELedPanelTarget } from './enums.js';

export const DisplaySchema = T.Object({
  brand: T.String({ minLength: 1, error: 'Nome da marca da tela é um campo obrigatório' }),
  model: T.String({ minLength: 1, error: 'Modelo da tela é um campo obrigatório' }),
  size: T.Optional(T.String({ minLength: 1, error: 'Tamanho da tela é um campo obrigatório' })),
  code: T.String({ minLength: 1, error: 'Identificador da tela é um campo obrigatório' }),
  orientation: T.String({ minLength: 1, error: 'Orientação da tela é um campo obrigatório' }),
  type: T.String({ minLength: 1, error: 'tipo da tela é um campo obrigatório' }),
  externalId: T.String({ minLength: 1, error: 'Identificador do dispositivo é um campo obrigatório' }),
});

export const ModemSchema = T.Object({
  marca: T.Optional(T.String({ minLength: 1, error: 'Nome da marca do modem é um campo obrigatório' })),
  modelo: T.Optional(T.String({ minLength: 1, error: 'Modelo do modem é um campo obrigatório' })),
  identifierNumber: T.Optional(
    T.String({ minLength: 1, error: 'Número de identificação do modem é um campo obrigatório' })
  ),
  imei: T.Optional(T.String({ minLength: 1, error: 'IMEI do modem é um campo obrigatório' })),
  msisdn: T.Optional(T.String({ minLength: 1, error: 'Número de telefone do modem é um campo obrigatório' })),
  monthlyDataLimit: T.Optional(T.String({ minLength: 1, error: 'Limite de dados do modem é um campo obrigatório' })),
});

export const CreateMaterialSchema = T.Object({
  materialTypeUuid: T.String({ format: 'uuid', error: 'O tipo de material é obrigatório' }),
  name: T.String({ minLength: 1, error: 'Nome do material de pdv é um campo obrigatório' }),
  contractType: T.Optional(T.String()),
  materialType: T.String({ error: 'O tipo de material é obrigatório' }),
  brand: T.String({ format: 'uuid', error: 'A marca é um campo obrigatório do tipo uuid' }),
  cnpj: T.String({ error: 'CNPJ é um campo obrigatório' }),
  facility: T.String({ format: 'uuid', error: 'O estabelecimento é um campo obrigatório do tipo uuid' }),
  exhibitionPoint: T.Union([
    T.String({ minLength: 3, error: 'Ponto de referência deve ter no mínimo 3 caracteres' }),
    T.Undefined(),
  ]),
  // Display is optional when deviceManagerType is FIREBASE
  display: T.Optional(DisplaySchema),
  device: T.Optional(T.String({ format: 'uuid', error: 'Device é um campo uuid' })),
  defaultCampaignUuid: T.String({ format: 'uuid', error: 'UUID da campanha padrão é um campo obrigatório' }),
  scheduleCampaigns: T.Optional(
    T.Array(
      T.Object({
        uuid: T.String({ format: 'uuid', error: 'UUID da campanha é um campo obrigatório' }),
        startDate: T.String({ error: 'Data de início é um campo obrigatório' }),
        endDate: T.String({ error: 'Data de fim é um campo obrigatório' }),
      })
    )
  ),
  currentCampaignUuid: T.Optional(
    T.String({ format: 'uuid', error: 'UUID da campanha padrão é um campo obrigatório' })
  ), //TODO ver essa regra. Provavelmente não é obrigatória, deve mostrar no front o que está passando no momento
  deviceManagerType: T.Optional(T.Enum(EDeviceManagerType, { default: EDeviceManagerType.MAGICINFO })),
  firebaseVerificationCode: T.Optional(
    T.String({
      minLength: 8,
      maxLength: 8,
      pattern: '^[A-Z0-9]{8}$',
      error: 'Código de verificação deve ter 8 caracteres alfanuméricos',
    })
  ),
  ledControllerId: T.Optional(
    T.String({
      maxLength: 12,
      error: 'Id do controlador deve ter no maximo 12 caracteres',
    })
  ),
  ledPanelWidthPx: T.Optional(T.Integer({ minimum: 1, error: 'Largura do painel deve ser um número positivo' })),
  ledPanelHeightPx: T.Optional(T.Integer({ minimum: 1, error: 'Altura do painel deve ser um número positivo' })),
  ledPanelOffsetXPx: T.Optional(T.Integer({ error: 'Deslocamento X deve ser um número inteiro' })),
  ledPanelOffsetYPx: T.Optional(T.Integer({ error: 'Deslocamento Y deve ser um número inteiro' })),
  ledPanelOutputResolution: T.Optional(
    T.Enum(ELedPanelOutputResolution, { error: 'Resolução de saída da tvbox é obrigatória' })
  ),
  ledPanelTarget: T.Optional(T.Enum(ELedPanelTarget, { error: 'Alvo do painel LED inválido' })),
  ledPanelFps: T.Optional(T.Integer({ minimum: 1, maximum: 60, error: 'FPS deve ser um número entre 1 e 60' })),
  tags: T.Optional(T.Array(T.String({ format: 'uuid', error: 'Tag é um campo uuid' }))),
  modem: T.Optional(ModemSchema),
  installedAt: T.Optional(T.String()),
});

export type CreateMaterialInputType = typeof CreateMaterialSchema.static;
export type DisplaySchemaType = typeof DisplaySchema.static;

export async function ValidateCreateMaterialInput(value: unknown): Promise<CreateMaterialInputType> {
  return Value.Parse(CreateMaterialSchema, value);
}

export function checkMaterialCreateInput(field: string, value: unknown): string | boolean {
  return validateField(CreateMaterialSchema, field, value);
}
